import { useNavigate } from 'react-router-dom';

import { IAdminNavCounter } from '@/shared/constants';

const MAX_BADGE_COUNT = 99;

interface ICounterBadgeEvent {
  key?: string;
  preventDefault: () => void;
  stopPropagation: () => void;
}

interface IProps {
  count: number;
  counter: IAdminNavCounter;
  onNavigate?: () => void;
}

/**
 * Бейдж-счётчик пункта меню админки. С `href` и `label` — кликабельный: ведёт в отфильтрованный
 * список (`<span role="link">`, потому что `<a>` нельзя вложить в `NavLink` пункта); без них —
 * просто число. Больше 99 — «99+». `tone: 'warning'` — оранжевый вариант для второй очереди пункта.
 * @param root0 Пропсы
 * @param root0.count Значение счётчика (ненулевое)
 * @param root0.counter Описание бейджа из ADMIN_NAV_ITEMS
 * @param root0.onNavigate Вызывается после перехода по бейджу (закрыть мобильное меню)
 */
export const AdminSidebarCounterBadge = ({
  count,
  counter,
  onNavigate
}: IProps) => {
  const navigate = useNavigate();
  const badgeLabel = count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : count;
  const className = `sidebar__item-badge${counter.tone ? ` sidebar__item-badge--${counter.tone}` : ''}`;

  // Без подписи role="link" останется без доступного имени для скринридера —
  // в этом случае откатываемся на обычный (некликабельный) бейдж
  if (!counter.href || !counter.label) {
    return <span className={className}>{badgeLabel}</span>;
  }

  const { href } = counter;

  /**
   * Переходит по ссылке бейджа, не задевая клик по самому пункту меню.
   * @param event Событие клика/клавиатуры по бейджу
   */
  const goToHref = (event: ICounterBadgeEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(href);
    onNavigate?.();
  };

  return (
    <span
      className={`${className} sidebar__item-badge--link`}
      role='link'
      tabIndex={0}
      aria-label={`${counter.label}: ${count}`}
      title={counter.label}
      onClick={goToHref}
      // Enter/Space — как ожидается от role="link"
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') goToHref(event);
      }}
    >
      {badgeLabel}
    </span>
  );
};
